import { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { Close, Search } from '@mui/icons-material';
import { alpha, Box, InputAdornment, List, ListItemButton, ListItemText, TextField, Typography } from '@mui/material';
import { useFormikContext } from 'formik';

import { PermissoesModulosChip } from 'pages/cadastro/permissoes/components/form/modulos/PermissoesModulosChip';
import { PermissoesProps } from 'pages/cadastro/permissoes/types';
import { getEnabledCount, getModuleStatus } from 'pages/cadastro/permissoes/utils';

export type PermissoesModulosMenu = {
    selectedModuleId: number | null;
    setSelectedModuleId: Dispatch<SetStateAction<number | null>>;
    setPermSearch: Dispatch<SetStateAction<string>>;
};

export function PermissoesModulosMenu({ selectedModuleId, setSelectedModuleId, setPermSearch }: PermissoesModulosMenu) {
    const { values } = useFormikContext<PermissoesProps>();
    const totalModules = values.grupoUsuarioRecursoPermissoes.length;
    const [moduleSearch, setModuleSearch] = useState('');

    /**
     * Conta quantos módulos tem pelo menos uma permissão habilitada
     */
    const modulesWithAccess = useMemo(
        () => values.grupoUsuarioRecursoPermissoes.filter((m) => getEnabledCount(m) > 0).length,
        [values.grupoUsuarioRecursoPermissoes],
    );

    /**
     * Filtra os módulos pelo termo de busca
     */
    const filteredModules = useMemo(
        () => values.grupoUsuarioRecursoPermissoes.filter((m) => m.text.toLowerCase().includes(moduleSearch.toLowerCase())),
        [values.grupoUsuarioRecursoPermissoes, moduleSearch],
    );

    return (
        <Box sx={{ width: 280, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ py: 1.5, px: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Buscar módulo..."
                    value={moduleSearch}
                    onChange={(e) => setModuleSearch(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                            endAdornment: moduleSearch ? (
                                <InputAdornment position="end">
                                    <Close sx={{ fontSize: 16, cursor: 'pointer', color: 'text.disabled' }} onClick={() => setModuleSearch('')} />
                                </InputAdornment>
                            ) : undefined,
                        },
                    }}
                />
            </Box>

            <List disablePadding sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
                {filteredModules.map((module) => {
                    const enabled = getEnabledCount(module);
                    const total = module.permissoes.length;
                    const { status, label, color } = getModuleStatus(enabled, total);
                    const isActive = module.idRecurso === selectedModuleId;

                    return (
                        <ListItemButton
                            key={module.idRecurso}
                            selected={isActive}
                            onClick={() => {
                                setSelectedModuleId(module.idRecurso);

                                setPermSearch('');
                            }}
                            sx={{
                                borderRadius: 1,
                                mb: 0.5,
                                gap: 1,
                                py: 0,
                                px: 1.5,
                                border: 1,
                                borderColor: (theme) => (isActive ? alpha(theme.palette.primary.main, 0.5) : 'transparent'),
                            }}
                        >
                            <ListItemText
                                primary={module.text}
                                secondary={label}
                                slotProps={{
                                    primary: {
                                        sx: {
                                            fontWeight: isActive ? 700 : 400,
                                            lineHeight: 1.4,
                                            fontSize: 14,
                                            color: isActive ? 'secondary.main' : 'text.primary',
                                        },
                                    },
                                    secondary: {
                                        sx: { color, fontWeight: 500, fontSize: 12 },
                                    },
                                }}
                            />

                            <PermissoesModulosChip label={enabled} status={status} />
                        </ListItemButton>
                    );
                })}

                {filteredModules.length === 0 && (
                    <Typography variant="body2" color="text.disabled" sx={{ p: 2, textAlign: 'center' }}>
                        Nenhum módulo encontrado
                    </Typography>
                )}
            </List>

            <Box
                sx={{
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    px: 1.5,
                    py: 1,
                    flexShrink: 0,
                    textAlign: 'center',
                }}
            >
                <Typography variant="body2" color="text.disabled">
                    {modulesWithAccess} de {totalModules} módulos com acesso
                </Typography>
            </Box>
        </Box>
    );
}
